import { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Loader2, Mail, Building, Shield, CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { validateInvitation, acceptInvitation } from '@/api/invitations.api';

// Password validation schema: min 8 chars, 1 uppercase, 1 number, 1 symbol
const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter (A-Z)')
  .regex(/[0-9]/, 'Password must contain at least one number (0-9)')
  .regex(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~`]/, 'Password must contain at least one symbol (!@#$%^&*)');

const acceptInviteSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
    terms_accepted: z.boolean().refine((val) => val === true, {
      message: 'You must agree to the Terms of Service and Privacy Policy',
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type AcceptInviteFormData = z.infer<typeof acceptInviteSchema>;

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const token = searchParams.get('token');

  const form = useForm<AcceptInviteFormData>({
    resolver: zodResolver(acceptInviteSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
      terms_accepted: false,
    },
  });

  // Validate invitation token
  const { data: validationResult, isLoading: isValidating, error: validationError } = useQuery({
    queryKey: ['invitation-validate', token],
    queryFn: () => {
      if (!token) throw new Error('No invitation token provided');
      return validateInvitation(token);
    },
    enabled: !!token,
    retry: false,
  });

  // Auto-accept if user is logged in and invitation is valid
  const autoAcceptMutation = useMutation({
    mutationFn: async () => {
      if (!token) throw new Error('No invitation token provided');
      return acceptInvitation(token, {
        password: '', // Not needed for logged-in users
        terms_accepted: true,
      });
    },
    onSuccess: (response) => {
      toast({
        title: 'Welcome!',
        description: `You've successfully joined ${response.workspace.name}`,
      });

      // Redirect to workspace
      if (response.workspace.slug) {
        navigate(`/${response.workspace.slug}`);
      } else if (response.workspace.id) {
        navigate(`/w/${response.workspace.id}`);
      } else {
        navigate('/dashboard');
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message || 'Failed to accept invitation',
        variant: 'destructive',
      });
    },
  });

  // Accept invitation mutation (for new users)
  const acceptMutation = useMutation({
    mutationFn: async (data: { password: string; terms_accepted: boolean }) => {
      if (!token) throw new Error('No invitation token provided');
      return acceptInvitation(token, data);
    },
    onSuccess: (response) => {
      // Store JWT token
      if (response.token) {
        localStorage.setItem('bp_token', response.token);
      }

      toast({
        title: 'Welcome to BillPortal!',
        description: `You've successfully joined ${response.workspace.name}`,
      });

      // Redirect to workspace
      if (response.workspace.slug) {
        navigate(`/${response.workspace.slug}`);
      } else if (response.workspace.id) {
        navigate(`/w/${response.workspace.id}`);
      } else {
        navigate('/dashboard');
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message || 'Failed to accept invitation',
        variant: 'destructive',
      });
    },
  });

  // Auto-accept if user is logged in and invitation is valid
  useEffect(() => {
    if (
      user &&
      validationResult?.valid &&
      validationResult.invitation &&
      !autoAcceptMutation.isPending &&
      !autoAcceptMutation.isSuccess &&
      !autoAcceptMutation.isError
    ) {
      autoAcceptMutation.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, validationResult?.valid, validationResult?.invitation]);

  // Redirect if invalid or expired
  useEffect(() => {
    if (validationResult && !validationResult.valid && token) {
      navigate(`/invite/expired?token=${token}`);
    }
  }, [validationResult, token, navigate]);

  // Handle loading state
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Validating invitation...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Handle validation error
  if (validationError || (validationResult && !validationResult.valid)) {
    // This will be handled by the redirect effect, but show a fallback
    return null;
  }

  // If user is logged in, show loading while auto-accepting
  if (user && validationResult?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Accepting invitation...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const invitation = validationResult?.invitation;

  if (!invitation) {
    return null;
  }

  const onSubmit = (data: AcceptInviteFormData) => {
    acceptMutation.mutate({
      password: data.password,
      terms_accepted: data.terms_accepted,
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Mail className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Welcome to BillPortal!</CardTitle>
          <CardDescription className="text-base">
            {invitation.first_name
              ? `Hi ${invitation.first_name}, you've been invited to join a workspace`
              : "You've been invited to join a workspace"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Invitation Details */}
          <div className="space-y-4 rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building className="h-5 w-5 text-muted-foreground" />
                <span className="font-semibold">Workspace</span>
              </div>
              <span className="text-sm font-medium">{invitation.workspace.name}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-muted-foreground" />
                <span className="font-semibold">Role</span>
              </div>
              <span className="text-sm font-medium">{invitation.role}</span>
            </div>
          </div>

          {/* Password Setup Form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <PasswordInput
                        placeholder="Enter your password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                    {field.value && (
                      <PasswordStrengthMeter password={field.value} className="mt-2" />
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <PasswordInput
                        placeholder="Confirm your password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="terms_accepted"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-normal">
                        I agree to the{' '}
                        <a
                          href="/terms-of-service"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Terms of Service
                        </a>
                        {' '}and{' '}
                        <a
                          href="/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Privacy Policy
                        </a>
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={acceptMutation.isPending}
              >
                {acceptMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Accept Invitation & Create Account
                  </>
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
